import { useState, useEffect } from 'react'
import { X, AlertTriangle, MapPin } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, WorkOrder } from '@/types'
import { useCreateWorkOrderFromAlertMutation } from '@/store/api'

interface CreateWorkOrderModalProps {
  alert: Alert
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function CreateWorkOrderModal({
  alert,
  isOpen,
  onClose,
  onSuccess
}: CreateWorkOrderModalProps) {
  const [createWorkOrder, { isLoading }] = useCreateWorkOrderFromAlertMutation()
  
  const [formData, setFormData] = useState({
    title: `Maintenance Required: ${alert.title}`,
    description: alert.description + '\n\nGenerated from predictive maintenance alert.',
    priority: alert.priority === 'critical' ? 'critical' as const : 
              alert.priority === 'high' ? 'high' as const :
              alert.priority === 'warning' ? 'medium' as const : 'low' as const,
    type: 'corrective' as const,
    estimatedHours: alert.prediction ? 
      (alert.priority === 'critical' ? 4 : alert.priority === 'high' ? 3 : 2) : 2,
    estimatedCost: alert.prediction?.estimatedCost || 500,
    dueDate: '',
    skillsRequired: [] as string[],
    assignedTo: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      // Set due date based on priority and prediction
      const now = new Date()
      const dueDate = new Date()
      
      if (alert.prediction?.timeToFailure) {
        // Parse time to failure and set due date before predicted failure
        const timeMatch = alert.prediction.timeToFailure.match(/(\d+)\s*(days?|weeks?|months?)/i)
        if (timeMatch) {
          const value = parseInt(timeMatch[1])
          const unit = timeMatch[2].toLowerCase()
          
          if (unit.includes('day')) {
            dueDate.setDate(now.getDate() + Math.max(1, value - 1))
          } else if (unit.includes('week')) {
            dueDate.setDate(now.getDate() + Math.max(1, (value * 7) - 2))
          } else if (unit.includes('month')) {
            dueDate.setMonth(now.getMonth() + Math.max(1, value - 1))
          }
        }
      } else {
        // Default based on priority
        switch (alert.priority) {
          case 'critical':
            dueDate.setHours(now.getHours() + 4) // 4 hours
            break
          case 'high':
            dueDate.setDate(now.getDate() + 1) // 1 day
            break
          case 'warning':
            dueDate.setDate(now.getDate() + 3) // 3 days
            break
          default:
            dueDate.setDate(now.getDate() + 7) // 1 week
        }
      }
      
      setFormData(prev => ({
        ...prev,
        dueDate: dueDate.toISOString().slice(0, 16) // Format for datetime-local input
      }))

      // Set recommended skills based on equipment type
      const equipmentType = alert.equipment?.type
      const skillsMap: Record<string, string[]> = {
        hvac: ['HVAC Technician', 'Electrical'],
        plumbing: ['Plumbing', 'General Maintenance'],
        electrical: ['Electrical', 'Safety Certified'],
        elevators: ['Elevator Technician', 'Electrical', 'Safety Certified'],
        security: ['Security Systems', 'Electrical'],
        other: ['General Maintenance']
      }
      
      setFormData(prev => ({
        ...prev,
        skillsRequired: skillsMap[equipmentType as keyof typeof skillsMap] || ['General Maintenance']
      }))
    }
  }, [isOpen, alert])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required'
    } else if (new Date(formData.dueDate) < new Date()) {
      newErrors.dueDate = 'Due date cannot be in the past'
    }
    
    if (formData.estimatedHours <= 0) {
      newErrors.estimatedHours = 'Estimated hours must be greater than 0'
    }
    
    if (formData.estimatedCost <= 0) {
      newErrors.estimatedCost = 'Estimated cost must be greater than 0'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    try {
      const workOrderData: Partial<WorkOrder> = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        type: formData.type,
        equipmentId: alert.equipmentId,
        estimatedHours: formData.estimatedHours,
        estimatedCost: formData.estimatedCost,
        dueDate: formData.dueDate,
        skillsRequired: formData.skillsRequired,
        assignedTo: formData.assignedTo ? { id: formData.assignedTo } as any : undefined,
      }
      
      await createWorkOrder({
        alertId: alert.id,
        workOrderData
      }).unwrap()
      
      onSuccess()
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to create work order:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const toggleSkill = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      skillsRequired: prev.skillsRequired.includes(skill)
        ? prev.skillsRequired.filter(s => s !== skill)
        : [...prev.skillsRequired, skill]
    }))
  }

  const availableSkills = [
    'HVAC Technician',
    'Plumbing',
    'Electrical',
    'Elevator Technician',
    'Security Systems',
    'General Maintenance',
    'Safety Certified',
    'Specialized Equipment'
  ]

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-trust-blue" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create Work Order</h2>
              <p className="text-sm text-gray-600">From Alert: {alert.title}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Alert Summary */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-sm">Alert Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span>{alert.equipment?.name} - {alert.equipment?.location?.building}</span>
                </div>
                <Badge variant={alert.priority === 'critical' ? 'critical' : 'warning'}>
                  {(alert.priority || 'unknown').charAt(0).toUpperCase() + (alert.priority || 'unknown').slice(1)}
                </Badge>
              </div>
              {alert.prediction && (
                <div className="text-sm text-gray-600">
                  <strong>Predicted failure:</strong> {alert.prediction.timeToFailure} 
                  (Confidence: {alert.prediction.confidence}%)
                </div>
              )}
            </CardContent>
          </Card>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Order Title *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter work order title"
                className={errors.title ? 'border-red-300' : ''}
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe the work to be performed"
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-trust-blue focus:border-trust-blue ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-trust-blue focus:border-trust-blue"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Work Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-trust-blue focus:border-trust-blue"
              >
                <option value="corrective">Corrective</option>
                <option value="preventive">Preventive</option>
                <option value="emergency">Emergency</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <Input
                type="datetime-local"
                value={formData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                className={errors.dueDate ? 'border-red-300' : ''}
              />
              {errors.dueDate && (
                <p className="text-sm text-red-600 mt-1">{errors.dueDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign to Technician
              </label>
              <select
                value={formData.assignedTo}
                onChange={(e) => handleChange('assignedTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-trust-blue focus:border-trust-blue"
              >
                <option value="">Select technician (optional)</option>
                <option value="tech1">John Smith - HVAC Specialist</option>
                <option value="tech2">Sarah Johnson - Electrical</option>
                <option value="tech3">Mike Davis - General Maintenance</option>
                <option value="tech4">Lisa Chen - Plumbing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Hours *
              </label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={formData.estimatedHours}
                onChange={(e) => handleChange('estimatedHours', parseFloat(e.target.value))}
                className={errors.estimatedHours ? 'border-red-300' : ''}
              />
              {errors.estimatedHours && (
                <p className="text-sm text-red-600 mt-1">{errors.estimatedHours}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Cost ($) *
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.estimatedCost}
                onChange={(e) => handleChange('estimatedCost', parseFloat(e.target.value))}
                className={errors.estimatedCost ? 'border-red-300' : ''}
              />
              {errors.estimatedCost && (
                <p className="text-sm text-red-600 mt-1">{errors.estimatedCost}</p>
              )}
            </div>
          </div>

          {/* Skills Required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Skills Required
            </label>
            <div className="flex flex-wrap gap-2">
              {availableSkills.map((skill) => (
                <Badge
                  key={skill}
                  variant={formData.skillsRequired.includes(skill) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleSkill(skill)}
                >
                  {skill}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Click to select/deselect required skills
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Work Order'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}